// DESIGN-SOURCE: none — not in Figma — route container: it resolves the metric, pulls the data and decides where a dismissal lands, and renders no surface of its own. The surfaces are MetricExplanationModal and TransferValueModal, which MainView dispatches to.
/**
 * MainContainer — Route-mounted screen that renders a single KPI breakdown
 * for the player page. SE-5024 wraps the existing breakdown modals
 * (MetricExplanationModal, TransferValueModal) in expo-router routes so the
 * KPI tile presses on the player page hero open them as full-page
 * navigations instead of in-place modal state.
 *
 * The route param `metric` is one of the BREAKDOWN_METRIC_SLUGS; an
 * unrecognized slug dismisses the route rather than rendering a broken page.
 * Dismissing pops when there is a route to pop and otherwise REPLACES into the
 * player page — see `handleClose` for why a bare `router.back()` is not enough
 * (SE-8303).
 */

import { useLocalSearchParams, usePathname, useRouter } from "expo-router";
import { useCallback, useEffect, useMemo } from "react";

import { useCurrentUser } from "@/features/auth";
import {
  CurrencyCode,
  usePlayerDetailListPlayersByTeamIdsAndPositionIdsQuery,
} from "@/generated/graphql";
import { extractIndicatorData } from "@/features/player-detail/utils/playerPageHeaderUtils";
import { usePlayerDetailQuery } from "@/hooks/usePlayerDetailQuery";
import { useTeamSelection } from "@/providers/TeamSelectionProvider";
import { getRosterValues } from "@/utils/playerDetailCenter";

import {
  SLUG_TO_BREAKDOWN_METRIC,
  isBreakdownMetricSlug,
  type BreakdownMetric,
} from "../../constants";

import MainView from "./MainView";

/**
 * Resolves the indicator type the breakdown route should render, or null when
 * the slug is unknown.
 * @param slug - URL slug captured by the [metric] dynamic segment
 * @returns The resolved indicator type, or null when the slug is unknown
 */
function resolveMetric(slug: string | undefined): BreakdownMetric | null {
  if (!slug || !isBreakdownMetricSlug(slug)) return null;
  return SLUG_TO_BREAKDOWN_METRIC[slug];
}

/** The path segment this route hangs off, used to find the player page above it. */
const BREAKDOWN_SEGMENT = "/breakdown/";

/**
 * Resolves the player page this breakdown sits on top of.
 *
 * Derived from the CURRENT pathname rather than assembled from `/players/`,
 * because the breakdown route is mounted in three trees — `players`,
 * `watchlist/players` and `search/players` — and a hardcoded prefix would throw
 * a reader out of whichever tree they were browsing. Falls back to the
 * standalone player tree when the pathname carries no breakdown segment, which
 * only happens when this screen is mounted in place rather than as a route.
 * @param pathname - The current route path.
 * @param playerId - The player the breakdown belongs to.
 * @returns The href of the player page to land on.
 */
function resolveParentHref(
  pathname: string,
  playerId: string | undefined
): string {
  const index = pathname.lastIndexOf(BREAKDOWN_SEGMENT);
  if (index > 0) return pathname.slice(0, index);
  return `/players/${playerId ?? ""}`;
}

/**
 * Static label format used by getRosterValues to pull the relevant per-team
 * percentile values out of the roster query payload.
 */
/**
 * Metrics whose value is a RANGE rather than a point, so there is no single
 * number to compare against a roster distribution. Both skip the roster query
 * and render no comparison chart. `transferValue` has always behaved this way;
 * SE-8157 added `potential`, whose value is a floor-to-ceiling band.
 * @param metric - The breakdown metric being rendered.
 * @returns True when the metric has no single comparable value.
 */
const isRangeValuedMetric = (metric: BreakdownMetric): boolean =>
  metric === "transferValue" || metric === "potential";

const ROSTER_LABEL_BY_METRIC: Record<BreakdownMetric, string> = {
  gpr: "Gemini Player Rating",
  // Range-valued, like transferValue: a floor-to-ceiling band has no single
  // roster metric to compare against, so there is no roster label (SE-8157).
  potential: "",
  playerStyleFit: "Player Style Fit",
  teamStyleFit: "Team Style Fit",
  transferValue: "",
};

/**
 * Stable metric id used by MetricExplanationModal to look up the prose for a
 * given metric. `transferValue` is not driven through this map — it has its
 * own dedicated modal.
 */
const METRIC_ID_BY_BREAKDOWN: Record<BreakdownMetric, string | null> = {
  gpr: "gemini_player_rating",
  // SE-8159 supplies this explanation entry; SE-8158 then replaces the generic
  // explanation modal with the dedicated GprPotentialModal. Until both land a
  // `potential` route resolves and renders the explanation shell with no copy
  // behind this id — the route is wired here, the content arrives there.
  potential: "gpr_potential",
  playerStyleFit: "player_style_fit",
  teamStyleFit: "team_style_fit",
  transferValue: null,
};

/**
 * Props for MainContainer. All optional — when omitted the breakdown resolves
 * its player + metric from the route params (standalone breakdown route). When
 * provided, the breakdown renders in-place on top of its caller (e.g. the
 * player preview modal) and `onClose` dismisses it instead of popping a route.
 * (SE-5473)
 */
interface MainContainerProps {
  /** Player id to render the breakdown for; falls back to the route param. */
  readonly playerId?: string;
  /** Breakdown metric slug; falls back to the route param. */
  readonly metricSlug?: string;
  /**
   * Close handler. Omit it for the routed usage, which dismisses through
   * `handleClose` — popping when there is a route to pop, replacing into the
   * player page when there is not.
   */
  readonly onClose?: () => void;
}

/**
 * MainContainer pulls player + roster data for the breakdown route and hands
 * everything to MainView which dispatches to the right modal.
 * @param props - Optional in-place overrides (SE-5473); omitted for route usage.
 * @param props.playerId - Player id override, falls back to the route param.
 * @param props.metricSlug - Metric slug override, falls back to the route param.
 * @param props.onClose - Close handler override; omitted, the route dismisses
 * through `handleClose` (pop, else replace into the player page).
 */
const MainContainer = ({
  playerId: playerIdProp,
  metricSlug: metricSlugProp,
  onClose: onCloseProp,
}: MainContainerProps = {}) => {
  // 1. VARIABLES & DERIVED STATE
  const router = useRouter();
  const routeParams = useLocalSearchParams<{
    playerId: string;
    metric: string;
  }>();
  const playerId = playerIdProp ?? routeParams.playerId;
  const metricSlug = metricSlugProp ?? routeParams.metric;
  const metric = resolveMetric(metricSlug);
  const pathname = usePathname();
  const parentHref = resolveParentHref(pathname, playerId);

  const { isLoaded: isTeamSelectionLoaded } = useTeamSelection();
  const currentUser = useCurrentUser();
  const currencyCode =
    currentUser?.organization?.currencyCode ?? CurrencyCode.Eur;

  const { data } = usePlayerDetailQuery({
    variables: { id: playerId ?? "" },
    skip: !isTeamSelectionLoaded || !playerId,
  });
  const player = data?.getPlayer ?? null;

  const indicatorData = useMemo(() => extractIndicatorData(player), [player]);

  const teamIds = useMemo(
    () =>
      (currentUser?.organization?.organizationTeams ?? []).map(l => l.teamId),
    [currentUser?.organization?.organizationTeams]
  );
  const positionIds = useMemo(
    () =>
      (player?.positions ?? []).map(p => p?.id).filter((p): p is string => !!p),
    [player?.positions]
  );

  // Skip the roster comparison query for transferValue — its modal doesn't
  // consume rosterValues, and for missing teamIds/positionIds where it would
  // return nothing useful anyway.
  const {
    data: listPlayerSameTeamAndPosition,
    loading: isFetchingListPlayerSameTeamAndPosition,
  } = usePlayerDetailListPlayersByTeamIdsAndPositionIdsQuery({
    variables: { teamIds, positionIds },
    skip:
      !metric ||
      isRangeValuedMetric(metric) ||
      teamIds.length === 0 ||
      positionIds.length === 0,
  });

  const rawValueByMetric: Record<BreakdownMetric, number | null> = {
    gpr: indicatorData.gpr,
    playerStyleFit: indicatorData.playerStyleFit,
    teamStyleFit: indicatorData.teamStyleFit,
    transferValue: null,
    // Range-valued (floor..ceiling), so there is no single raw value to hand
    // the explanation modal — same reason transferValue is null (SE-8157).
    potential: null,
  };

  const rosterValues = useMemo(() => {
    if (!metric || isRangeValuedMetric(metric)) return [];
    const label = ROSTER_LABEL_BY_METRIC[metric];
    return getRosterValues(
      label,
      listPlayerSameTeamAndPosition,
      player?.positions?.map(p => ({ name: p?.name })) ?? null,
      player?.id
    );
  }, [metric, listPlayerSameTeamAndPosition, player?.positions, player?.id]);

  const handleClose = useCallback(() => {
    if (onCloseProp) {
      onCloseProp();
      return;
    }
    // SE-7947 set `backBehavior="history"` on the bottom tabs navigator, so
    // `router.back()` now follows real navigation history instead of resetting
    // to the first tab. That is the right app-wide behaviour, and it removed
    // the accidental escape hatch this guard was leaning on: a breakdown URL
    // opened COLD — a stale or mistyped link followed from outside the app, or
    // a hard reload — has no navigator history behind it, so `back()` had
    // nothing to pop and silently no-opped, parking the reader on a breakdown
    // URL that renders nothing.
    //
    // Preferring the real back stack and falling back to a deterministic
    // destination is the shape every other dead-end close in this repo already
    // uses (`ContractScreenContainer`, `StandardHeaderBackButtonContainer`).
    if (router.canGoBack()) {
      router.back();
      return;
    }
    router.replace(parentHref);
  }, [onCloseProp, parentHref, router]);

  // Routed usage is the breakdown route — a native `transparentModal` — so the
  // explanation modal renders in it instead of presenting a second native
  // modal mid-transition (SE-8770). In-place callers (the preview modal) own
  // their dismissal through onClose and are not a route.
  const hostIsModalRoute = !onCloseProp;

  const metricId = metric ? METRIC_ID_BY_BREAKDOWN[metric] : null;
  const metricValue = metric ? rawValueByMetric[metric] : null;

  // 2. SIDE EFFECTS
  // Guard against unknown slugs — dismissing is friendlier than rendering an
  // empty modal shell. In-place callers close via onClose; the route dismisses
  // through `handleClose`, which pops when it can and otherwise replaces into
  // the player page. It must NOT be narrowed back to a bare `router.back()`:
  // a cold-loaded breakdown has no navigator history and that is a no-op
  // (SE-8303).
  useEffect(() => {
    if (!metric) {
      handleClose();
    }
  }, [metric, handleClose]);

  // 3. RETURN
  if (!metric) return null;

  return (
    <MainView
      metric={metric}
      metricId={metricId}
      metricValue={metricValue}
      rosterValues={rosterValues}
      player={player}
      listPlayerSameTeamAndPosition={listPlayerSameTeamAndPosition}
      isFetchingListPlayerSameTeamAndPosition={
        isFetchingListPlayerSameTeamAndPosition
      }
      currencyCode={currencyCode}
      onClose={handleClose}
      hostIsModalRoute={hostIsModalRoute}
    />
  );
};

MainContainer.displayName = "PlayerMetricBreakdownMainContainer";

export default MainContainer;
